import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import Badge from '@/decodo/components/atoms/badge';
import BadgeStrapi, {
  BadgeStrapiProps,
} from '@/decodo/components/atoms/badge/BadgeStrapi';
import LinkButton from '@/decodo/components/atoms/LinkButton';
import { Text } from '@/decodo/components/atoms/Text';
import { gradientDecodoBorderStyling } from '@/styles/gradient';

import { ctaContainerStyles, pricingCardContainerStyles } from './styles';

export type PricingCardProps = {
  id: number;
  title: string;
  discount?: string;
  discountLabel?: string;
  price: number;
  priceOld?: number;
  pricePer: 'IP' | 'GB';
  priceTotal?: number;
  priceTotalOld?: number;
  priceTotalPrefixText?: string;
  priceTotalPostfixText?: string;
  buttonText: string;
  buttonVariant?: 'filled' | 'outlined';
  url: string;
  outlineColor?: 'grey' | 'black' | 'brand' | 'gradient';
  outlineText?: string;
  icon?: React.ReactNode;
  showMoneyBack?: boolean;
  alternativeFootnote?: string;
  topBadge?: BadgeStrapiProps;
};

export const outlineClasses = {
  grey: '',
  black: '', // remove from strapi after rebranding
  brand: 'border-brand-300 dark:border-brand-500 dark:bg-brand-975 bg-brand-50',
  gradient: `${gradientDecodoBorderStyling({
    color: 'gradient-main',
    borderRadius: 'rounded',
    borderWidth: 'p-px',
  })}`,
} as const;

export default function PricingCard({
  title,
  discount,
  discountLabel,
  price,
  priceOld,
  pricePer,
  priceTotal,
  priceTotalPrefixText,
  priceTotalPostfixText,
  priceTotalOld,
  buttonText,
  buttonVariant,
  url,
  outlineColor = 'grey',
  outlineText,
  icon,
  showMoneyBack, // remove from strapi after rebranding
  alternativeFootnote,
  topBadge,
}: PricingCardProps) {
  const { texts } = useTranslation();

  const planId = url?.split('planId=')[1] || '';
  const trackId = `${title}-${planId}`;

  const formattedPricePer = pricePer.replaceAll('-', ' ').replaceAll('ww', '');

  const transformNumberToWithOneDecimal = (number: number) => {
    const numberString = number.toString();
    if (numberString.includes('.')) {
      return numberString;
    }
    if (numberString === '0') {
      return numberString;
    }
    return number.toFixed(1);
  };

  return (
    <div
      data-tracker-component='card'
      className={cn(
        pricingCardContainerStyles,
        outlineClasses[outlineColor],
        !!outlineText && 'mt-[28px] rounded-t-none'
      )}
    >
      {outlineText && (
        <span
          className={cn(
            'text-label dark:text-base-dark-white bg-base-white dark:bg-gray-dark-900 absolute -left-px -right-px top-0 z-[1] -translate-y-full rounded-t border py-1.5 text-center font-serif uppercase text-gray-950',
            outlineColor && outlineClasses[outlineColor]
          )}
        >
          {outlineText}
        </span>
      )}
      <div className='mb-4 flex w-full items-center justify-between'>
        <Text
          classNames={{
            wrapper: 'dark:text-base-dark-white font-serif text-h4',
          }}
        >
          {title}
        </Text>

        <div className={cn('flex gap-2', !discount && 'invisible')}>
          {topBadge && <BadgeStrapi block={topBadge} />}
          <Badge colors='success-100' variant='filled'>
            {discount && (
              <>
                {discountLabel} {discount}
              </>
            )}
          </Badge>
        </div>
      </div>

      <div className='dark:text-base-dark-white mb-4 flex items-baseline'>
        <Text classNames={{ wrapper: 'font-serif text-h2' }}>
          ${transformNumberToWithOneDecimal(price)}
        </Text>
        <Text classNames={{ wrapper: 'leading-[3rem] font-serif text-h4' }}>
          /{formattedPricePer}
        </Text>
      </div>

      <p className='text-body-m dark:text-gray-dark-400 mb-6 flex max-h-5 min-h-5 items-center justify-center gap-1'>
        {priceTotalPrefixText && <span>{priceTotalPrefixText}</span>}
        {priceTotal && priceTotalPostfixText && (
          <span>
            <span className='text-body-m-bold'>${priceTotal}</span>{' '}
            {priceTotalPostfixText}
          </span>
        )}
      </p>

      <p
        className={cn(
          'text-label-s dark:bg-gray-dark-800 text-gray-dark-400 mb-3 max-h-5 min-h-5 w-full text-nowrap rounded bg-gray-50 p-1 text-center font-serif uppercase',
          outlineColor === 'brand' &&
            'bg-brand-100 dark:bg-brand-950 text-brand-500',
          !alternativeFootnote && 'opacity-0'
        )}
      >
        {alternativeFootnote}
      </p>

      <div className={ctaContainerStyles}>
        <LinkButton
          data-tracker-context={trackId}
          href={url}
          variant={buttonVariant || 'filled'}
          color='primary'
          slim={false}
          fullwidth
          openInNewTab={false}
          rightSection={
            texts.pricing_cards.contact_sales !== buttonText && icon
          }
          leftSection={texts.pricing_cards.contact_sales === buttonText && icon}
          classNames={{
            root: 'group',
            inner: 'flex items-center justify-center gap-4',
            rightSection:
              'group-hover:translate-x-2 duration-300 ease-in-out transition',
          }}
        >
          {buttonText}
        </LinkButton>
      </div>
    </div>
  );
}
